import { useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import { useMedia } from 'react-use';
import {
  ServingSizeWarningModal,
  getShowServingSizeWarninigModal,
} from '@hungryroot/app-mhr';
import { type UseCartMutations } from '@hungryroot/cart';
import { useToast, Toast, ToastVariant } from '@hungryroot/components-toast';
import { AccountStatus } from '@hungryroot/customer';
import {
  getWarningText,
  type DietaryRestrictionsText,
} from '@hungryroot/dietary-restrictions';

import { ThumbsDown, ThumbsUp } from '@hungryroot/icons';
import { type OrderIngredient } from '@hungryroot/orders';
import { WarningText, WarningTextStyleVariant } from '@hungryroot/pho';
import { Button, Paragraph } from '@hungryroot/ramen';
import { useCustomerRecipeRatingById } from '@hungryroot/ratings';
import {
  Divider,
  Footer,
  IngredientsList,
  Instructions,
  PairingLoader,
  RDPModal as BaseRDPModal,
  RecipeBookmarkButton,
  ShareButtonContainer,
  XButton,
  formatNutritionValue,
  nutritionDataToNutritionTable,
  stringNumberToNumberNoDecimal,
  Header,
  PDPModal,
  Nutrition,
} from '@hungryroot/rdp';
import {
  type PairingBundle,
  type PairingDetail,
  type ProductDetailList,
  type PairingV1 as Pairing,
} from '@hungryroot/rest';

import { useSaladShooter } from '@hungryroot/salad-shooter';
import { segmentAnalytics } from '@hungryroot/segment';
import { BreakpointQueryLarge } from '@hungryroot/tokens';
import { useCustomerServingSize } from '../../../api/v2/customers/[id]/pricing_config/get/get-customer-pricing-config';
import { AddToCartModal } from '../../../pages/my-hungryroot/components/AddToCartModal/AddToCartModal';
import { useAddToCartModal } from '../../../pages/my-hungryroot/components/AddToCartModal/AddToCartModalHook';
import {
  getCount,
  getInCart,
} from '../../cards/ProductCard/BindToApplicationProductCard';
import { getCartPairingBundle } from '../../cart/query';
import {
  convertOrderIngredientToCartProduct,
  convertPairingToCartPairing,
} from '../../cart/transforms';
import { useCustomization } from '../../CustomizationProvider/CustomizationProvider';
import {
  type PairingWithIngredientDetails,
  type EditRecipeOrderDetail,
} from '../../edit-recipe/hooks/useEditRecipe/useEditRecipe';
import { useEditRecipeMutations } from '../../edit-recipe/hooks/useEditRecipeMutations/useEditRecipeMutations';
import {
  PairingEdit,
  getDisplayPoints,
} from '../../edit-recipe/PairingEdit/PairingEdit';

import { usePdpModal } from './usePdpModal';
import styles from './RDPModal.module.css';

const getRatingsCount = (count: number): string => {
  if (count < 1000) return `${count}`;
  if (count === 1000) return '1k';
  return '1k+';
};

const useProductInfo = ({
  currentPairing,
  pairingV2,
}: {
  currentPairing: PairingWithIngredientDetails;
  pairingV2: PairingDetail | undefined;
}) => {
  // get nutrition only when pairingV2 is received
  const nutrition = pairingV2?.nutrition;

  const { recipe_score, num_ratings, cooking_time, servings, id } =
    currentPairing;

  const isFavorited = useCustomerRecipeRatingById(id);

  const ratingConfig =
    isFavorited !== undefined
      ? {
          label: 'Rating',
          value: (
            <span
              className={clsx(
                styles['rating-icon'],
                styles[isFavorited ? 'liked' : 'disliked']
              )}
            >
              {isFavorited ? <ThumbsUp /> : <ThumbsDown />}
            </span>
          ),
        }
      : {
          label: 'Rating',
          value: recipe_score
            ? `${stringNumberToNumberNoDecimal(
                recipe_score.toString()
              )}% (${getRatingsCount(num_ratings)})`
            : 'New',
        };

  return [
    ratingConfig,
    {
      label: 'Cook time',
      value: `${cooking_time} min`,
    },
    {
      label: 'Serving',
      value: `${servings}`,
    },
    {
      label: 'Cal per serv.',
      value: `${nutrition?.calories !== undefined ? nutrition?.calories : ''}`,
    },
  ];
};

export interface RDPModalProps {
  initialPairing: Pairing;
  orderId?: number;
  onDismiss: () => void;
  initialBundle?: {
    order_details: EditRecipeOrderDetail[];
    points: PairingBundle['points'];
  };
  testid?: string;
  index?: number | null;
  onProductContentScroll?: (scrollDepth: number) => void;
}

export function RDPModal({
  initialPairing,
  orderId,
  onDismiss,
  initialBundle,
  testid,
  // the index here is for salad shooter tracking purposes; however, this has not been implemented
  // every place, so it's set as null by default to signal to the data team the data sent is not
  // part of the for you experience
  index = null,
  onProductContentScroll,
}: RDPModalProps) {
  const customization = useCustomization();
  const [isEditOpen, setIsEditOpen] = useState(false);
  const cartMutations =
    'mutations' in customization ? customization.mutations : null;
  const { createBundle, updateBundle } = useEditRecipeMutations();
  const [isEditRecipeModalOpen, setIsEditRecipeModalOpen] = useState(false);
  const [isUpdateCartModalOpen, setIsUpdateCartModalOpen] = useState(false);
  const useAddToCartModalQuery = useAddToCartModal();
  const customerPlanServingSize = useCustomerServingSize();

  const [isServingSizeWarningModalOpen, setIsServingSizeWarningModalOpen] =
    useState(false);

  const { toast } = useToast();
  const updateCartCallback = useRef<
    null | ((cartMutations: UseCartMutations | null) => void)
  >(null);
  const { accountStatus, isLoading: isLoadingAccountStatus } =
    useAddToCartModalQuery;
  let count = 0;
  // The following is to get current cart count to pass down to AddRemoveButton
  const inCart =
    'cart' in customization
      ? getInCart(initialPairing.id, true, customization)
      : false;

  if (inCart && 'cart' in customization) {
    count = getCount(initialPairing.id, true, customization);
  }

  const isDesktop = useMedia(BreakpointQueryLarge);

  const isSkipped = accountStatus === AccountStatus.Skipped;
  const isCancelled = accountStatus === AccountStatus.Cancelled;
  const isPaused = accountStatus === AccountStatus.Paused;

  const isAccountInActive = isSkipped || isCancelled || isPaused;

  // the account status is still in a skipped/cancelled/paused state when the user confirms
  // in the update cart modal, so we need to check the account status as a side effect to ensure
  // the action the user is taking reflects their must up-to-date account status
  useEffect(() => {
    if (
      updateCartCallback &&
      accountStatus === AccountStatus.Active &&
      cartMutations !== null
    ) {
      updateCartCallback.current?.(cartMutations);
      updateCartCallback.current = null;
    }
  }, [updateCartCallback, cartMutations, accountStatus]);

  const saladShooter = useSaladShooter();

  return (
    <PairingEdit
      // to ensure consumers of this in other RDPs pass an actual orderId, but actually
      // clear why this is necessary
      orderId={orderId}
      initialBundle={initialBundle}
      initialPairing={initialPairing}
    >
      {({
        pairing: currentPairing,
        dietaryIngredients,
        ingredientContainsNeverPeriodicity,
        editRecipeQuery,
      }) => {
        const {
          featured_img: featured_img_url,
          name,
          points,
          badges,
          servings,
          cooking_time,
          ingredients,
          id,
          slug,
          short_instruction_html,
        } = currentPairing;

        const isEdited = editRecipeQuery.isEdited;

        const showServingSizeWarningModal = getShowServingSizeWarninigModal(
          currentPairing.servings,
          customerPlanServingSize
        );

        const containsDietaryIngredients = dietaryIngredients.length > 0;

        const getWarning = (
          dietaryIngredients: DietaryRestrictionsText[],
          ingredientContainsNeverPeriodicity: boolean
        ) => {
          if (containsDietaryIngredients) {
            return {
              warningText: `This recipe contains ${getWarningText(
                dietaryIngredients
              )}`,
              warningTextIntensity: WarningTextStyleVariant.Critical,
            };
          } else if (ingredientContainsNeverPeriodicity) {
            return {
              warningText: `This recipe contains items you set to 'never'`,
              warningTextIntensity: WarningTextStyleVariant.Moderate,
            };
          }
        };

        const warning = getWarning(
          dietaryIngredients,
          ingredientContainsNeverPeriodicity
        );

        return (
          <PairingLoader id={currentPairing.id}>
            {({ pairing: pairingV2 }) => {
              // get nutrition only when pairingV2 is received
              const nutrition = pairingV2?.nutrition;

              const productInfo = useProductInfo({
                currentPairing,
                pairingV2,
              });

              const onAddClick = (cartMutations: UseCartMutations | null) => {
                // what to do when volume is exceeded?
                if (editRecipeQuery.mutations.length > 0) {
                  createBundle({
                    modifications: editRecipeQuery.mutations,
                    pairing: convertPairingToCartPairing(
                      currentPairing as Pairing
                    ),
                    pairingIngredients: currentPairing.ingredients.map(
                      (ingredient) => {
                        return convertOrderIngredientToCartProduct(
                          ingredient as OrderIngredient
                        );
                      }
                    ),
                  });
                } else {
                  cartMutations?.addPairing({
                    pairing: currentPairing as Pairing,
                    dietaryProductIngredients: dietaryIngredients,
                  });
                }
              };

              const shareButton = (
                <ShareButtonContainer
                  title={name}
                  text={name}
                  url={`${window.location.origin}/recipes/${slug}/`}
                  onShareSuccess={() => {
                    saladShooter.track([
                      {
                        kind: 'ui-action',
                        action: {
                          kind: 'shared',
                          target: { kind: 'pairing', id },
                          position: index,
                          shared_from: 'rdp',
                        },
                      },
                    ]);
                    if (isDesktop) {
                      toast(
                        <Toast variant={ToastVariant.Success}>
                          Link copied
                        </Toast>
                      );
                    }
                  }}
                  onShareError={() => {
                    if (isDesktop) {
                      toast(
                        <Toast variant={ToastVariant.Critical}>
                          Unable to copy link
                        </Toast>
                      );
                    }
                  }}
                />
              );

              const bookmarkButton = (
                <RecipeBookmarkButton
                  pairingId={id}
                  pairingName={name}
                  onBookmark={() => {
                    saladShooter.track([
                      {
                        kind: 'ui-action',
                        action: {
                          kind: 'saved',
                          target: { kind: 'pairing', id },
                          position: index,
                          saved_from: 'rdp',
                        },
                      },
                    ]);
                  }}
                />
              );

              const staticRecipeBookmarkButton = (
                <RecipeBookmarkButton
                  pairingId={id}
                  pairingName={name}
                  size="medium"
                  prominence="tertiary"
                  style="muted"
                  onBookmark={() => {
                    saladShooter.track([
                      {
                        kind: 'ui-action',
                        action: {
                          kind: 'saved',
                          target: { kind: 'pairing', id },
                          position: index,
                          saved_from: 'rdp',
                        },
                      },
                    ]);
                  }}
                />
              );

              const staticXButton = (
                <XButton
                  onClick={() => {
                    onDismiss();
                  }}
                  size="medium"
                  prominence="tertiary"
                  style="muted"
                  testid="rdp-modal-scroll-x-button"
                />
              );

              const staticShareButton = (
                <ShareButtonContainer
                  title={name}
                  text={name}
                  size="medium"
                  prominence="tertiary"
                  style="muted"
                  url={`${window.location.origin}/recipes/${slug}/`}
                  onShareSuccess={() => {
                    saladShooter.track([
                      {
                        kind: 'ui-action',
                        action: {
                          kind: 'shared',
                          target: { kind: 'pairing', id },
                          position: index,
                          shared_from: 'rdp',
                        },
                      },
                    ]);
                    if (isDesktop) {
                      toast(
                        <Toast variant={ToastVariant.Success}>
                          Link copied
                        </Toast>
                      );
                    }
                  }}
                />
              );

              const { props: pdpProps, openPdp } = usePdpModal({
                onClose: onDismiss,
              });

              return (
                <>
                  {pdpProps?.id && (
                    <PDPModal
                      {...pdpProps}
                      onShareSuccess={() => {
                        saladShooter.track([
                          {
                            kind: 'ui-action',
                            action: {
                              kind: 'shared',
                              target: { kind: 'product', id: pdpProps.id },
                              position: index,
                              shared_from: 'pdp',
                            },
                          },
                        ]);
                      }}
                      onFavorite={() => {
                        saladShooter.track([
                          {
                            kind: 'ui-action',
                            action: {
                              kind: 'saved',
                              target: { kind: 'product', id: pdpProps.id },
                              position: index,
                              saved_from: 'pdp',
                            },
                          },
                        ]);
                      }}
                      createFooterButton={
                        pdpProps.createFooterButton
                          ? (product) => {
                              const onAdd = () => {
                                saladShooter.track([
                                  {
                                    kind: 'ui-action',
                                    action: {
                                      kind: 'added-to-cart',
                                      target: {
                                        kind: 'product',
                                        id,
                                      },
                                      position: index,
                                      added_from: 'shop',
                                    },
                                  },
                                ]);
                              };

                              const onRemove = () => {
                                saladShooter.track([
                                  {
                                    kind: 'ui-action',
                                    action: {
                                      kind: 'removed-from-cart',
                                      target: {
                                        kind: 'product',
                                        id,
                                      },
                                      position: index,
                                      removed_from: 'shop',
                                    },
                                  },
                                ]);
                              };
                              return pdpProps?.createFooterButton?.(product, {
                                onAdd,
                                onRemove,
                                onIncrement: onAdd,
                                onSubtract: onRemove,
                              });
                            }
                          : undefined
                      }
                      onImageScroll={(imageIndex) => {
                        saladShooter.track([
                          {
                            kind: 'ui-action',
                            action: {
                              kind: 'detail-image-scroll',
                              target: {
                                kind: 'pairing',
                                id,
                              },
                              position: index,
                              image_idx: imageIndex,
                              scrolled_on: 'pdp',
                            },
                          },
                        ]);
                      }}
                      onProductContentScroll={(scrollDepth) => {
                        saladShooter.track([
                          {
                            kind: 'ui-action',
                            action: {
                              kind: 'detail-text-scroll',
                              target: {
                                kind: 'pairing',
                                id,
                              },
                              position: index,
                              scrolled_on: 'pdp',
                              scroll_depth: scrollDepth,
                            },
                          },
                        ]);
                      }}
                      onShowMoreNutrition={(isExpanded) => {
                        saladShooter.track([
                          {
                            kind: 'ui-action',
                            action: {
                              kind: isExpanded
                                ? 'expanded-nutrition'
                                : 'collapsed-nutrition',
                              target: {
                                kind: 'product',
                                id,
                              },
                              position: index,
                            },
                          },
                        ]);
                      }}
                      onBackClick={() => {
                        pdpProps.onBackClick?.();
                      }}
                      showOSNSection={true}
                    />
                  )}
                  <BaseRDPModal
                    onProductContentScroll={onProductContentScroll}
                    testid={testid ?? 'modal-rdp'}
                    header={
                      <Header
                        primaryTopIconButton={staticShareButton}
                        secondaryTopIconButton={staticRecipeBookmarkButton}
                        primaryTopRightIconButton={staticXButton}
                      />
                    }
                    isEdited={isEdited}
                    ariaLabel={name}
                    isOpen={true}
                    imageUrl={
                      featured_img_url === null ? undefined : featured_img_url
                    }
                    title={name}
                    credits={points}
                    // do i actually need the reduce here -- can i just map?
                    ingredients={ingredients.reduce(
                      (acc, ingredient) => {
                        if (
                          editRecipeQuery.bundle.order_details &&
                          editRecipeQuery.bundle.order_details.find((p) => {
                            return p.product === ingredient.id;
                          })
                        ) {
                          acc.push(ingredient as unknown as ProductDetailList);
                          return acc;
                        }
                        return acc;
                      },
                      [] as PairingDetail['ingredients']
                    )}
                    primaryTopIconButton={shareButton}
                    warningComponent={
                      warning && (
                        <WarningText intensity={warning?.warningTextIntensity}>
                          {warning?.warningText}
                        </WarningText>
                      )
                    }
                    secondaryTopIconButton={bookmarkButton}
                    primaryTopRightIconButton={
                      <XButton
                        onClick={() => {
                          onDismiss();
                        }}
                        testid="rdp-modal-x-button"
                      />
                    }
                    productInfo={productInfo}
                    badges={badges}
                    onDismiss={onDismiss}
                  >
                    <Divider />
                    <IngredientsList
                      onClick={openPdp}
                      ingredients={
                        currentPairing
                          ? currentPairing.ingredients.map((ingredient) => {
                              const isRemoved = !(
                                editRecipeQuery.bundle.order_details &&
                                editRecipeQuery.bundle.order_details.find(
                                  (p) => {
                                    return p.product === ingredient.id;
                                  }
                                )
                              );

                              return {
                                id: ingredient.id,
                                name: ingredient.name,
                                imageUrl: ingredient.packaged_image_url,
                                subText: ingredient.brand_name,
                                isRemoved,
                                amount: ingredient.amount,
                              };
                            })
                          : []
                      }
                    />
                    <div className={styles.button}>
                      {isLoadingAccountStatus === false && (
                        <Button
                          width="full-width"
                          size="medium"
                          style="default"
                          prominence="secondary"
                          textPrimary="Edit Ingredients"
                          onClick={() => {
                            saladShooter.track([
                              {
                                kind: 'ui-action',
                                action: {
                                  kind: 'editing-ingredients',
                                  target: { kind: 'pairing', id },
                                  position: index,
                                },
                              },
                            ]);
                            isSkipped || isPaused || isCancelled
                              ? setIsEditRecipeModalOpen(true)
                              : setIsEditOpen(true);
                          }}
                        ></Button>
                      )}
                    </div>
                    <Divider />
                    <Nutrition
                      onShowMoreNutrition={(isExpanded) => {
                        saladShooter.track([
                          {
                            kind: 'ui-action',
                            action: {
                              kind: isExpanded
                                ? 'collapsed-nutrition'
                                : 'expanded-nutrition',
                              target: {
                                kind: 'pairing',
                                id,
                              },
                              position: index,
                            },
                          },
                        ]);
                      }}
                      editWarning={
                        isEdited
                          ? 'Nutrition may not accurately reflect any edits made to the ingredients.'
                          : ''
                      }
                      servings={
                        <>
                          <Paragraph className={styles['serving-size']}>
                            Servings {servings}
                          </Paragraph>
                          <Paragraph className={styles['amount-per-serving']}>
                            Amount per serving
                          </Paragraph>
                        </>
                      }
                      productInfo={[
                        {
                          label: 'Calories',
                          value: `${
                            nutrition
                              ? stringNumberToNumberNoDecimal(
                                  nutrition.calories.toString()
                                )
                              : ''
                          }`,
                        },
                        {
                          label: 'Fat',
                          value: `${
                            nutrition
                              ? formatNutritionValue(
                                  nutrition.total_fat,
                                  'total_fat'
                                )
                              : ''
                          }`,
                        },
                        {
                          label: 'Carbs',
                          value: `${
                            nutrition
                              ? formatNutritionValue(
                                  nutrition.total_carb,
                                  'total_carb'
                                )
                              : ''
                          }`,
                        },
                        {
                          label: 'Protein',
                          value: `${
                            nutrition
                              ? formatNutritionValue(
                                  nutrition.protein,
                                  'protein'
                                )
                              : ''
                          }`,
                        },
                        {
                          label: 'Sugars',
                          value: `${
                            nutrition
                              ? formatNutritionValue(nutrition.sugars, 'sugars')
                              : ''
                          }`,
                        },
                      ]}
                      sections={
                        nutrition
                          ? nutritionDataToNutritionTable(nutrition)
                          : []
                      }
                      disclaimer="The % Daily Value (DV) is based on a 2,000 calorie diet. Calorie amount includes all olive oil mentioned in the recipe."
                    />
                    <Divider />
                    <Instructions
                      disclaimer={
                        isEdited
                          ? 'Instructions may not accurately reflect any edits made to the ingredients.'
                          : ''
                      }
                      instructions={short_instruction_html}
                      cookTime={cooking_time}
                    />
                    {orderId ? (
                      <Footer
                        outOfStock={currentPairing.is_merchandized === false}
                        isLoading={cartMutations?.isMutating}
                        onIncrementClick={() => {
                          saladShooter.track([
                            {
                              kind: 'ui-action',
                              action: {
                                kind: 'added-to-cart',
                                target: {
                                  kind: 'pairing',
                                  id,
                                },
                                position: index,
                                added_from: 'rdp',
                              },
                            },
                          ]);

                          sendInteractionEvent({
                            orderId,
                            id,
                            name,
                            interactionType: 'add_to_cart',
                          });

                          if (isAccountInActive) {
                            showServingSizeWarningModal
                              ? setIsServingSizeWarningModalOpen(true)
                              : setIsUpdateCartModalOpen(true);
                            updateCartCallback.current = onAddClick;
                          } else {
                            onAddClick(cartMutations);
                          }
                        }}
                        onAddClick={() => {
                          saladShooter.track([
                            {
                              kind: 'ui-action',
                              action: {
                                kind: 'added-to-cart',
                                target: {
                                  kind: 'pairing',
                                  id,
                                },
                                position: index,
                                added_from: 'rdp',
                              },
                            },
                          ]);

                          sendInteractionEvent({
                            orderId,
                            id,
                            name,
                            interactionType: 'add_to_cart',
                          });

                          if (isAccountInActive) {
                            showServingSizeWarningModal
                              ? setIsServingSizeWarningModalOpen(true)
                              : setIsUpdateCartModalOpen(true);
                            updateCartCallback.current = (cartMutations) => {
                              onAddClick(cartMutations);
                              onDismiss();
                            };
                          } else {
                            onAddClick(cartMutations);
                            onDismiss();
                          }
                        }}
                        onRemoveClick={() => {
                          saladShooter.track([
                            {
                              kind: 'ui-action',
                              action: {
                                kind: 'removed-from-cart',
                                target: {
                                  kind: 'pairing',
                                  id,
                                },
                                position: index,
                                removed_from: 'rdp',
                              },
                            },
                          ]);

                          sendInteractionEvent({
                            orderId,
                            id,
                            name,
                            interactionType: 'remove_from_cart',
                          });

                          const onRemoveClick = (
                            cartMutations: UseCartMutations | null
                          ) => {
                            cartMutations?.removePairing(currentPairing);
                            onDismiss();
                          };
                          if (isAccountInActive) {
                            showServingSizeWarningModal
                              ? setIsServingSizeWarningModalOpen(true)
                              : setIsUpdateCartModalOpen(true);
                            updateCartCallback.current = onRemoveClick;
                          } else {
                            onRemoveClick(cartMutations);
                          }
                        }}
                        onSubtractClick={() => {
                          saladShooter.track([
                            {
                              kind: 'ui-action',
                              action: {
                                kind: 'removed-from-cart',
                                target: {
                                  kind: 'pairing',
                                  id,
                                },
                                position: index,
                                removed_from: 'rdp',
                              },
                            },
                          ]);

                          sendInteractionEvent({
                            orderId,
                            id,
                            name,
                            interactionType: 'remove_from_cart',
                          });

                          const onSubtractClick = (
                            cartMutations: UseCartMutations | null
                          ) => {
                            cartMutations?.removePairing(currentPairing);
                          };
                          if (isAccountInActive) {
                            showServingSizeWarningModal
                              ? setIsServingSizeWarningModalOpen(true)
                              : setIsUpdateCartModalOpen(true);
                            updateCartCallback.current = onSubtractClick;
                          } else {
                            onSubtractClick(cartMutations);
                          }
                        }}
                        creditsForItem={Number(
                          getDisplayPoints({
                            bundle: undefined,
                            pairing: currentPairing,
                            modifications: editRecipeQuery.mutations,
                            initialPairing,
                          })
                        )}
                        numInCart={count}
                      />
                    ) : null}
                  </BaseRDPModal>
                  {isEditOpen && (
                    <PairingEdit.IngredientEditorModal
                      index={index}
                      contentClassName={styles['recipe-editor']}
                      testid="edit-recipe-modal"
                      isOpen={isEditOpen}
                      onDismiss={() => {
                        setIsEditOpen(false);
                      }}
                      preventResetProducts={true}
                      isModalEditor={true}
                      isCustomModalEditor={true}
                      onSubmitUpdate={async (params) => {
                        const {
                          modifications,
                          cart,
                          pairing: paramPairing,
                        } = params;

                        try {
                          const { id: bundleId } = getCartPairingBundle(
                            cart,
                            initialPairing.id
                          );

                          try {
                            onDismiss();
                            await updateBundle({
                              bundleId,
                              pairing: paramPairing,
                              modifications,
                            });
                          } catch {
                            // if update bundle doesn't work, just dismiss modal
                            onDismiss();
                          }
                        } catch {
                          // if bundle id isn't found in cart, then it's a new pairing
                          setIsEditOpen(false);
                        }
                      }}
                    />
                  )}
                  {isServingSizeWarningModalOpen && (
                    <ServingSizeWarningModal
                      testid="rdp-plan-serving-size-modal"
                      heading={`You added a ${currentPairing.servings} serving recipe`}
                      onActionClick={() => {
                        setIsServingSizeWarningModalOpen(false);
                        setIsUpdateCartModalOpen(true);
                      }}
                      onDismiss={() => {
                        setIsServingSizeWarningModalOpen(false);
                        setIsUpdateCartModalOpen(true);
                      }}
                    />
                  )}
                  {(isEditRecipeModalOpen || isUpdateCartModalOpen) && (
                    <AddToCartModal
                      initialShowModal={
                        isEditRecipeModalOpen || isUpdateCartModalOpen
                      }
                      accountStatus={accountStatus}
                      orderId={orderId}
                      onAccountChangeSuccess={async () => {
                        if (isEditRecipeModalOpen) {
                          setIsEditOpen(true);
                        }
                      }}
                      onCancel={() => {
                        setIsEditRecipeModalOpen(false);
                        setIsUpdateCartModalOpen(false);
                      }}
                      unskipAndEdit={isEditRecipeModalOpen}
                    />
                  )}
                </>
              );
            }}
          </PairingLoader>
        );
      }}
    </PairingEdit>
  );
}

type TriggerInteractionEventArgs = {
  orderId: number;
  id: number;
  name: string;
  interactionType: 'add_to_cart' | 'remove_from_cart';
};

function sendInteractionEvent({
  orderId,
  id,
  name,
  interactionType,
}: TriggerInteractionEventArgs) {
  segmentAnalytics.track('Pairing Interaction', {
    event_name: 'Pairing Interaction',
    order_id: orderId,
    pairing_id: id,
    pairing_name: name,
    interaction_type: interactionType,
    page_name: 'RDP',
  });
}
